

// client/src/components/dashboard/DashboardInvoicesTable.jsx

import React, { useState } from "react";

const STATUS_CONFIG = {
  PAID:           { label: "Paid",           bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0", dot: "#10b981" },
  UNPAID:         { label: "Unpaid",         bg: "#fef2f2", color: "#dc2626", border: "#fecaca", dot: "#ef4444" },
  PARTIALLY_PAID: { label: "Partially Paid", bg: "#fffbeb", color: "#b45309", border: "#fde68a", dot: "#f59e0b" },
};

const COLUMNS = [
  { key: "invoice_number",           label: "Invoice Number"  },
  { key: "vendor",                   label: "Vendor"          },
  { key: "service_period",           label: "Service Period"  },
  { key: "billing_quarter",          label: "Billing Quarter" },
  { key: "payment_status",           label: "Status"          },
  { key: "payment_date",             label: "Payment Date"    },
  { key: "payment_reference_number", label: "Reference No."   },
];

const PAGE_SIZE = 10;

export default function DashboardInvoicesTable({ invoices }) {

  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [page,    setPage]    = useState(1);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
    setPage(1);
  };

  const sorted = [...(invoices || [])].sort((a, b) => {
    if (!sortKey) return 0;
    const av = a[sortKey] || "";
    const bv = b[sortKey] || "";
    return sortDir === "asc"
      ? av.toString().localeCompare(bv.toString())
      : bv.toString().localeCompare(av.toString());
  });

  const totalPages  = Math.ceil(sorted.length / PAGE_SIZE);
  const paginated   = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const needsScroll = sorted.length > PAGE_SIZE;

  // ── EMPTY STATE ──
  if (!invoices || invoices.length === 0) {
    return (
      <div style={styles.emptyWrap}>
        <div style={styles.emptyIcon}>🔍</div>
        <div style={styles.emptyTitle}>No records to display</div>
        <div style={styles.emptyHint}>
          Apply one or more filters above to load invoice records
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Smooth-scroll keyframe injected once */}
      <style>{`
        .sisl-tbody tr {
          transition: background-color 0.12s ease;
        }
        .sisl-scroll::-webkit-scrollbar {
          height: 5px;
        }
        .sisl-scroll::-webkit-scrollbar-track {
          background: #f1f5f9;
        }
        .sisl-scroll::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 999px;
        }
        .sisl-scroll::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        .sisl-scroll {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f1f5f9;
          scroll-behavior: smooth;
        }
        .sisl-row:hover td {
          background-color: #f0f9ff !important;
        }
      `}</style>

      {/* ── SCROLLABLE TABLE AREA ── */}
      <div
        className="sisl-scroll"
        style={{
          ...styles.tableWrap,
          maxHeight: needsScroll ? "540px" : "none",
          overflowY: needsScroll ? "auto"  : "visible",
        }}
      >
        <table style={styles.table}>

          {/* STICKY HEADER */}
          <thead style={styles.thead}>
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{
                    ...styles.th,
                    ...(sortKey === col.key ? styles.thActive : {}),
                  }}
                  onClick={() => handleSort(col.key)}
                >
                  <div style={styles.thInner}>
                    {col.label}
                    <span style={{
                      ...styles.sortIcon,
                      ...(sortKey === col.key ? styles.sortIconActive : {}),
                    }}>
                      {sortKey === col.key
                        ? sortDir === "asc" ? "↑" : "↓"
                        : "↕"}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          {/* BODY */}
          <tbody className="sisl-tbody">
            {paginated.map((invoice, i) => {
              const status = STATUS_CONFIG[invoice.payment_status] || {
                label: invoice.payment_status || "—",
                bg: "#f3f4f6", color: "#6b7280", border: "#e5e7eb", dot: "#d1d5db",
              };
              const isEven = i % 2 === 0;
              return (
                <tr
                  key={invoice._id || i}
                  className="sisl-row"
                  style={{ backgroundColor: isEven ? "#ffffff" : "#fafafa" }}
                >
                  <td style={styles.td}>
                    <span style={styles.invoiceNum}>{invoice.invoice_number || "—"}</span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.vendorBadge}>{invoice.vendor || "—"}</span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.monoText}>{invoice.service_period || "—"}</span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.quarterChip}>{invoice.billing_quarter || "—"}</span>
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.statusBadge,
                      backgroundColor: status.bg,
                      color:           status.color,
                      borderColor:     status.border,
                    }}>
                      <span style={{ ...styles.statusDot, backgroundColor: status.dot }} />
                      {status.label}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.dateText}>{invoice.payment_date || "—"}</span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.refText}>{invoice.payment_reference_number || "—"}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── FOOTER ── */}
      <div style={styles.tableFooter}>

        {/* Record count */}
        <span style={styles.footerCount}>
          Showing{" "}
          <strong style={{ color: "#374151" }}>
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, sorted.length)}
          </strong>
          {" "}of{" "}
          <strong style={{ color: "#374151" }}>{sorted.length}</strong>
          {" "}record{sorted.length !== 1 ? "s" : ""}
        </span>

        {/* Pagination — only shown when > 10 records */}
        {needsScroll && (
          <div style={styles.pagination}>

            <button
              style={{ ...styles.pageBtn, ...(page === 1 ? styles.pageBtnDisabled : {}) }}
              onClick={() => setPage(1)}
              disabled={page === 1}
              title="First page"
            >«</button>

            <button
              style={{ ...styles.pageBtn, ...(page === 1 ? styles.pageBtnDisabled : {}) }}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              title="Previous page"
            >‹</button>

            {/* Page number pills */}
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push("…");
                acc.push(p);
                return acc;
              }, [])
              .map((item, idx) =>
                item === "…" ? (
                  <span key={`ellipsis-${idx}`} style={styles.pageEllipsis}>…</span>
                ) : (
                  <button
                    key={item}
                    style={{
                      ...styles.pageBtn,
                      ...(page === item ? styles.pageBtnActive : {}),
                    }}
                    onClick={() => setPage(item)}
                  >
                    {item}
                  </button>
                )
              )}

            <button
              style={{ ...styles.pageBtn, ...(page === totalPages ? styles.pageBtnDisabled : {}) }}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              title="Next page"
            >›</button>

            <button
              style={{ ...styles.pageBtn, ...(page === totalPages ? styles.pageBtnDisabled : {}) }}
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              title="Last page"
            >»</button>

          </div>
        )}
      </div>
    </>
  );
}

// ─────────────────────────────────────
// STYLES
// ─────────────────────────────────────
const styles = {

  emptyWrap: {
    display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    padding: "64px 24px",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  emptyIcon:  { fontSize: "32px", marginBottom: "12px", opacity: 0.4 },
  emptyTitle: { fontSize: "14px", fontWeight: "700", color: "#111827", marginBottom: "6px" },
  emptyHint:  { fontSize: "13px", color: "#9ca3af", fontWeight: "400", textAlign: "center", maxWidth: "320px" },

  tableWrap: {
    overflowX: "auto",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    transition: "max-height 0.3s ease",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "13px",
    minWidth: "900px",
  },

  // Sticky header
  thead: {
    position: "sticky",
    top: 0,
    zIndex: 2,
  },

  th: {
    padding: "11px 16px",
    fontSize: "11px", fontWeight: "700",
    color: "#6b7280",
    textTransform: "uppercase", letterSpacing: "0.07em",
    backgroundColor: "#f9fafb",
    borderBottom: "2px solid #e5e7eb",
    textAlign: "left",
    whiteSpace: "nowrap",
    cursor: "pointer",
    userSelect: "none",
    transition: "color 0.15s",
    boxShadow: "0 1px 0 #e5e7eb",  // keeps border visible under sticky
  },
  thActive: { color: "#2563eb", backgroundColor: "#eff6ff" },
  thInner:  { display: "flex", alignItems: "center", gap: "5px" },
  sortIcon: {
    fontSize: "10px", color: "#d1d5db", fontWeight: "400",
    transition: "color 0.15s",
  },
  sortIconActive: { color: "#2563eb" },

  tr:     { transition: "background-color 0.12s ease" },
  trEven: { backgroundColor: "#ffffff" },
  trOdd:  { backgroundColor: "#fafafa" },

  td: {
    padding: "11px 16px",
    borderBottom: "1px solid #f3f4f6",
    verticalAlign: "middle",
  },

  invoiceNum: {
    fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
    fontSize: "12px", fontWeight: "600", color: "#1d4ed8",
    backgroundColor: "#eff6ff", padding: "2px 8px",
    borderRadius: "4px", border: "1px solid #dbeafe",
  },
  vendorBadge: {
    fontSize: "11px", fontWeight: "700",
    color: "#374151", backgroundColor: "#f3f4f6",
    padding: "3px 8px", borderRadius: "4px",
    letterSpacing: "0.04em",
  },
  monoText: {
    fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
    fontSize: "12px", color: "#4b5563",
  },
  quarterChip: {
    fontSize: "11px", fontWeight: "600",
    color: "#4f46e5", backgroundColor: "#ede9fe",
    padding: "3px 8px", borderRadius: "4px",
    border: "1px solid #ddd6fe",
  },
  statusBadge: {
    display: "inline-flex", alignItems: "center", gap: "5px",
    fontSize: "11px", fontWeight: "700",
    padding: "4px 10px", borderRadius: "999px",
    border: "1px solid transparent",
    whiteSpace: "nowrap",
  },
  statusDot: {
    width: "6px", height: "6px",
    borderRadius: "50%", flexShrink: 0,
  },
  dateText: { fontSize: "12px", color: "#6b7280", fontWeight: "400" },
  refText: {
    fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
    fontSize: "12px", color: "#9ca3af",
  },

  // ── FOOTER ──
  tableFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    borderTop: "1px solid #f3f4f6",
    backgroundColor: "#fafafa",
    flexWrap: "wrap",
    gap: "12px",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  footerCount: { fontSize: "12px", color: "#9ca3af", fontWeight: "400" },

  // ── PAGINATION ──
  pagination: { display: "flex", alignItems: "center", gap: "4px" },
  pageBtn: {
    minWidth: "30px", height: "30px",
    padding: "0 8px",
    fontSize: "12px", fontWeight: "600",
    color: "#374151",
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "6px",
    cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "background-color 0.12s, border-color 0.12s, color 0.12s",
    fontFamily: "inherit",
  },
  pageBtnActive: {
    backgroundColor: "#2563eb",
    borderColor: "#2563eb",
    color: "#ffffff",
  },
  pageBtnDisabled: {
    color: "#d1d5db",
    backgroundColor: "#fafafa",
    cursor: "not-allowed",
    borderColor: "#f3f4f6",
  },
  pageEllipsis: {
    fontSize: "12px", color: "#9ca3af",
    padding: "0 4px", lineHeight: "30px",
  },
};